import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import DashboardLayout from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import ClientMediaPanel from '@/components/ClientMediaPanel';
import AvanceCiclo from '@/components/AvanceCiclo';
import PortalCorporativo from '@/pages/corporativo/PortalCorporativo';
import { getDisplayTitle } from '@/lib/displayTitle';
import {
    Building2, ClipboardList, Loader2, CheckCircle2, XCircle, Clock, FileText, ArrowRight,
    Plus, Camera, AlertTriangle, Sparkles, FileDown, ShieldCheck, X, ExternalLink, Info,
    Mail, Phone, MapPin, IdCard, Layers, Image as ImageIcon, CreditCard,
} from 'lucide-react';

const ACCENT = '#1F8FB8'; // azul cliente
const METRIC_META = {
    rooms: { field: 'num_habitaciones', label: 'Número de habitaciones', unit: 'habitaciones' },
    tables: { field: 'num_mesas', label: 'Número de mesas', unit: 'mesas' },
    collaborators: { field: 'capacity_collaborators', label: 'Número de colaboradores', unit: 'colaboradores' },
    sqm: { field: 'm2_construccion', label: 'Metros cuadrados de construcción', unit: 'm²' },
};
const STATUS = {
    pending: { label: 'Pendiente', color: '#6B7280', bg: 'rgba(107,114,128,0.10)' },
    assigned: { label: 'Asignada', color: '#7C6BEF', bg: 'rgba(124,107,239,0.10)' },
    in_progress: { label: 'En curso', color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
    in_review: { label: 'En revisión', color: '#0EA5E9', bg: 'rgba(14,165,233,0.10)' },
    approved: { label: 'Aprobada', color: '#00875A', bg: 'rgba(0,135,90,0.10)' },
    rejected: { label: 'No aprobada', color: '#EF4444', bg: 'rgba(239,68,68,0.10)' },
};

function PanelClienteIndividual() {
    const { user, setUser } = useAuth();
    const [requests, setRequests] = useState([]);
    const [rubro, setRubro] = useState(null);
    const [loading, setLoading] = useState(true);
    const [selectedFilter, setSelectedFilter] = useState('total');
    const [detailModal, setDetailModal] = useState(null); // {type: 'checklist'|'certificate'|'action-plan', request}
    const [cycles, setCycles] = useState([]);
    const [distintivos, setDistintivos] = useState([]);
    const [savingCert, setSavingCert] = useState(false);

    useEffect(() => {
        api.get('/certifications')
            .then((r) => setRequests(r.data || []))
            .catch((e) => toast.error(apiErrorMessage(e, 'Error al cargar tus solicitudes')))
            .finally(() => setLoading(false));
        api.get('/me/cycles').then((r) => setCycles(r.data || [])).catch(() => setCycles([]));
        // Iter 51 Fase D · switched from /standards to /public/distintivos: users now
        // pick sellable bundles ("Distintivos"), not raw norms.
        api.get('/public/distintivos').then((r) => setDistintivos(r.data || [])).catch(() => setDistintivos([]));
    }, []);

    // Cargar rubro que coincide con el industry del cliente
    useEffect(() => {
        if (!user?.industry) return;
        api.get('/rubros')
            .then((r) => {
                const list = r.data || [];
                const target = user.industry.trim().toLowerCase();
                const match = list.find(
                    (x) => (x.name || '').trim().toLowerCase() === target
                        || (x.category || '').trim().toLowerCase() === target
                );
                if (match) setRubro(match);
            })
            .catch(() => {});
    }, [user?.industry]);

    const kpis = useMemo(() => {
        const total = requests.length;
        const inProgress = requests.filter((r) => ['assigned', 'in_progress', 'in_review', 'pending'].includes(r.status)).length;
        const approved = requests.filter((r) => r.status === 'approved' || r.admin_decision === 'approved').length;
        const rejected = requests.filter((r) => r.status === 'rejected' || r.admin_decision === 'rejected').length;
        return { total, inProgress, approved, rejected };
    }, [requests]);

    const filteredRequests = useMemo(() => {
        if (selectedFilter === 'total') return requests;
        if (selectedFilter === 'in_progress') return requests.filter((r) => ['assigned', 'in_progress', 'in_review', 'pending'].includes(r.status));
        if (selectedFilter === 'approved') return requests.filter((r) => r.status === 'approved' || r.admin_decision === 'approved');
        if (selectedFilter === 'rejected') return requests.filter((r) => r.status === 'rejected' || r.admin_decision === 'rejected');
        return requests;
    }, [requests, selectedFilter]);

    // Iter 76 · Issue 4/10 · el Distintivo comercial es el título; la norma
    // legal subyacente queda como dato secundario.
    const distLookup = useMemo(() => Object.fromEntries(distintivos.map((d) => [d.id, d])), [distintivos]);

    return (
        <DashboardLayout role={user?.role || 'client'}>
            {/* Info general del cliente */}
            <section className="mb-6 border p-5" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }} data-testid="client-info-card">
                <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: ACCENT }}>
                    ◆ Panel {user?.role === 'provider' ? 'Proveedor' : 'Cliente'}
                </div>
                <div className="flex items-start justify-between flex-wrap gap-3">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-display font-black">{user?.company || user?.name}</h1>
                        {user?.trade_name && <div className="text-sm opacity-70">Nombre comercial: {user.trade_name}</div>}
                        {(rubro || user?.industry) && (
                            <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 border-2 text-xs font-bold"
                                data-testid="client-rubro-chip"
                                style={{
                                    borderColor: rubro?.color || ACCENT,
                                    background: `${rubro?.color || ACCENT}10`,
                                    color: rubro?.color || ACCENT,
                                }}>
                                <Layers className="w-3.5 h-3.5" />
                                <span className="uppercase tracking-wider">Rubro:</span>
                                <span>{rubro?.name || user?.industry}</span>
                                {rubro?.category && rubro.category !== 'General' && (
                                    <span className="opacity-70">· {rubro.category}</span>
                                )}
                            </div>
                        )}
                    </div>
                    <Link to={user?.role === 'provider' ? '/provider/requests/new' : '/client/requests/new'}
                        data-testid="new-inspection-link"
                        className="text-white px-4 py-2.5 font-bold inline-flex items-center gap-2"
                        style={{ background: ACCENT }}>
                        <Plus className="w-4 h-4" /> Solicitar nuevo distintivo
                    </Link>
                </div>
                <div className="mt-2 text-[11px] opacity-60 italic">
                    ℹ️ Solicitas el Distintivo que deseas obtener. El administrador calendariza las auditorías de tu ciclo anual y asigna al auditor correspondiente.
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-xs">
                    <InfoField Icon={IdCard} label="RFC" value={user?.rfc} />
                    <InfoField Icon={Building2} label="Industria / Giro" value={user?.industry} />
                    <InfoField Icon={MapPin} label="Ciudad" value={[user?.city, user?.state].filter(Boolean).join(', ')} />
                    <InfoField Icon={Phone} label="Contacto" value={user?.phone} />
                    <InfoField Icon={Mail} label="Correo" value={user?.email} />
                    <InfoField Icon={MapPin} label="Dirección" value={user?.address} className="md:col-span-3" />
                </div>
            </section>

            {/* Iter 73 · Datos de tamaño para cotización — condicionado al rubro del cliente */}
            {rubro && (
                <SizeDataCard user={user} rubro={rubro} onSaved={async () => {
                    const me = await api.get('/auth/me');
                    setUser(me.data);
                }} />
            )}

            {/* Mi distintivo deseado — multi-select siempre visible */}
            <DesiredCertificationSection
                user={user}
                distintivos={distintivos}
                saving={savingCert}
                onToggle={async (distintivoId) => {
                    setSavingCert(true);
                    try {
                        const currentIds = user?.desired_distintivo_ids || [];
                        const nextIds = currentIds.includes(distintivoId)
                            ? currentIds.filter((x) => x !== distintivoId)
                            : [...currentIds, distintivoId];
                        await api.patch('/auth/me', { desired_distintivo_ids: nextIds });
                        const me = await api.get('/auth/me');
                        setUser(me.data);
                        toast.success(nextIds.length > currentIds.length ? 'Distintivo agregado' : 'Distintivo quitado');
                    } catch (e) {
                        toast.error(apiErrorMessage(e, 'No se pudo guardar la selección'));
                    } finally { setSavingCert(false); }
                }}
            />

            {/* Iter 64 · Identidad visual del negocio — logo + galería, self-service */}
            {user?.role === 'client' && user?.id && (
                <section className="mb-6 border p-5" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }} data-testid="client-identity-card">
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: ACCENT }}>
                        ◆ Identidad visual del negocio
                    </div>
                    <h2 className="text-2xl md:text-3xl font-display font-black mb-1">Logo y fotos del establecimiento</h2>
                    <p className="text-sm opacity-70 mb-5">
                        Sube el logotipo real de tu negocio y fotos que muestren tus instalaciones.
                        Cuando obtengas un distintivo se publicarán en tu ficha del catálogo público.
                    </p>
                    <AvanceCiclo />
                    <ClientMediaPanel userId={user.id} editable={true} />
                </section>
            )}
            {/* KPI cards clickeables */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                <KpiCard label="Total" value={kpis.total} Icon={ClipboardList} color={ACCENT}
                    active={selectedFilter === 'total'} onClick={() => setSelectedFilter('total')} testid="kpi-total" />
                <KpiCard label="En curso" value={kpis.inProgress} Icon={Clock} color="#F59E0B"
                    active={selectedFilter === 'in_progress'} onClick={() => setSelectedFilter('in_progress')} testid="kpi-inprogress" />
                <KpiCard label="Aprobadas" value={kpis.approved} Icon={CheckCircle2} color="#00875A"
                    active={selectedFilter === 'approved'} onClick={() => setSelectedFilter('approved')} testid="kpi-approved" />
                <KpiCard label="No aprobadas" value={kpis.rejected} Icon={XCircle} color="#EF4444"
                    active={selectedFilter === 'rejected'} onClick={() => setSelectedFilter('rejected')} testid="kpi-rejected" />
            </div>

            {/* Ciclos de distintivo (Fase B) */}
            {cycles.length > 0 && <CyclesWidget cycles={cycles} onRenewal={async (c) => {
                if (!window.confirm(`¿Iniciar renovación anual de ${c.distintivo_name || 'este Distintivo'}?\n\nSe creará un ciclo de 1 auditoría al 50% del precio original.`)) return;
                try {
                    await api.post(`/cycles/${c.id}/renewal`);
                    toast.success('Renovación anual iniciada');
                    const r = await api.get('/me/cycles');
                    setCycles(r.data || []);
                } catch (e) {
                    toast.error(apiErrorMessage(e, 'Error al iniciar renovación'));
                }
            }} />}

            {/* Lista filtrada */}
            <div className="mb-3 flex items-center justify-between">
                <h2 className="font-display font-black text-2xl">
                    {selectedFilter === 'total' && 'Todas las auditorías'}
                    {selectedFilter === 'in_progress' && 'Auditorías en curso'}
                    {selectedFilter === 'approved' && 'Distintivos aprobados'}
                    {selectedFilter === 'rejected' && 'Auditorías no aprobadas'}
                </h2>
                <div className="text-xs opacity-60">{filteredRequests.length} elementos</div>
            </div>

            {loading ? (
                <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>
            ) : filteredRequests.length === 0 ? (
                <EmptyState filter={selectedFilter} />
            ) : (
                <div className="space-y-3" data-testid="filtered-requests-list">
                    {filteredRequests.map((r) => (
                        <RequestCard key={r.id} req={r} filter={selectedFilter} distLookup={distLookup} onOpen={(type) => setDetailModal({ type, req: r })} />
                    ))}
                </div>
            )}

            {detailModal && (
                <DetailModal type={detailModal.type} req={detailModal.req} distLookup={distLookup} onClose={() => setDetailModal(null)} />
            )}
        </DashboardLayout>
    );
}

function InfoField({ Icon, label, value, className = '' }) {
    return (
        <div className={className}>
            <div className="flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold opacity-60 mb-0.5">
                <Icon className="w-3 h-3" /> {label}
            </div>
            <div className="text-sm font-bold truncate">{value || <span className="opacity-40">—</span>}</div>
        </div>
    );
}

/**
 * Iter 73 · dato de tamaño que el cliente captura como parte de su propio
 * onboarding — determina el rango de precio que le corresponde. El campo
 * mostrado depende de `rubro.pricing_metric` (habitaciones/mesas/
 * colaboradores/m²). El admin puede corregirlo después desde
 * /admin/clients/{id}.
 */
function SizeDataCard({ user, rubro, onSaved }) {
    const meta = METRIC_META[rubro?.pricing_metric] || METRIC_META.sqm;
    const [editing, setEditing] = useState(false);
    const [value, setValue] = useState(user?.[meta.field] ?? '');
    const [saving, setSaving] = useState(false);
    const hasValue = user?.[meta.field] != null;

    useEffect(() => { setValue(user?.[meta.field] ?? ''); }, [user, meta.field]);

    const save = async () => {
        const parsed = value === '' ? null : Number(value);
        if (parsed !== null && (Number.isNaN(parsed) || parsed < 0)) { toast.error('Valor inválido'); return; }
        setSaving(true);
        try {
            await api.patch('/auth/me', { [meta.field]: parsed });
            await onSaved();
            toast.success('Dato guardado');
            setEditing(false);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al guardar'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="mb-6 border p-5" data-testid="size-data-card"
            style={{ background: 'var(--card-bg)', borderColor: hasValue ? 'var(--border)' : '#F59E0B' }}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: ACCENT }}>◆ Datos para cotización</div>
                    <div className="text-sm font-bold">{meta.label}</div>
                    <p className="text-xs opacity-60 mt-0.5 max-w-md">
                        Este dato nos permite calcular el precio de tu auditoría según el tamaño de tu negocio.
                    </p>
                </div>
                {!editing ? (
                    <div className="flex items-center gap-3">
                        <div className="text-2xl font-display font-black" data-testid="size-data-value">
                            {hasValue ? `${user[meta.field]} ${meta.unit}` : <span className="text-sm font-normal opacity-50">Sin capturar</span>}
                        </div>
                        <button type="button" onClick={() => setEditing(true)} data-testid="size-data-edit-btn"
                            className="border-2 px-3 py-1.5 text-xs font-bold" style={{ borderColor: ACCENT, color: ACCENT }}>
                            {hasValue ? 'Editar' : 'Capturar'}
                        </button>
                    </div>
                ) : (
                    <div className="flex items-center gap-2">
                        <input type="number" min="0" value={value} onChange={(e) => setValue(e.target.value)}
                            data-testid="size-data-input" placeholder={meta.unit}
                            className="w-32 px-3 py-2 border-2 outline-none text-sm"
                            style={{ borderColor: ACCENT, background: 'var(--card-bg)' }} />
                        <button type="button" onClick={save} disabled={saving} data-testid="size-data-save-btn"
                            className="text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                            style={{ background: ACCENT }}>
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar'}
                        </button>
                        <button type="button" onClick={() => { setEditing(false); setValue(user?.[meta.field] ?? ''); }}
                            data-testid="size-data-cancel-btn"
                            className="border px-3 py-2 text-xs font-bold" style={{ borderColor: 'var(--border)' }}>
                            Cancelar
                        </button>
                    </div>
                )}
            </div>
        </section>
    );
}

function KpiCard({ label, value, Icon, color, active, onClick, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className="text-left p-5 border transition-all hover:shadow-lg"
            style={{
                background: active ? `${color}15` : 'var(--card-bg)',
                borderColor: active ? color : 'var(--border)',
                borderWidth: active ? '2px' : '1px',
            }}>
            <div className="flex items-center justify-between mb-2">
                <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">{label}</div>
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: `${color}18`, color }}>
                    <Icon className="w-5 h-5" />
                </div>
            </div>
            <div className="text-4xl font-display font-black" style={{ color }}>{value}</div>
        </button>
    );
}

function EmptyState({ filter }) {
    const meta = {
        total: { Icon: ClipboardList, msg: 'Aún no tienes auditorías. Solicita una nueva desde el botón superior.' },
        in_progress: { Icon: Clock, msg: 'No hay auditorías en curso en este momento.' },
        approved: { Icon: CheckCircle2, msg: 'Aún no tienes distintivos aprobados. ¡Sigue trabajando!' },
        rejected: { Icon: CheckCircle2, msg: '¡Felicidades! No tienes auditorías rechazadas.' },
    }[filter] || { Icon: ClipboardList, msg: 'Sin datos' };
    return (
        <div className="p-16 text-center border" style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}>
            <meta.Icon className="w-10 h-10 mx-auto opacity-40 mb-2" />
            <div className="opacity-70">{meta.msg}</div>
        </div>
    );
}

function RequestCard({ req, filter, distLookup, onOpen }) {
    const meta = STATUS[req.status] || STATUS.pending;
    const isApproved = req.status === 'approved' || req.admin_decision === 'approved';
    const isRejected = req.status === 'rejected' || req.admin_decision === 'rejected';
    const isInProgress = ['assigned', 'in_progress', 'in_review', 'pending'].includes(req.status);
    const { title: distTitle } = getDisplayTitle(req, distLookup);

    return (
        <div className="border p-4 flex flex-col md:flex-row md:items-center gap-3"
            style={{ background: 'var(--card-bg)', borderColor: 'var(--border)', borderLeftWidth: 4, borderLeftColor: meta.color }}
            data-testid={`request-card-${req.id}`}>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5"
                        style={{ background: meta.bg, color: meta.color }}>{meta.label}</span>
                    {req.score != null && (
                        <span className="text-[10px] uppercase tracking-widest font-bold opacity-80">Score: {req.score.toFixed(1)}%</span>
                    )}
                </div>
                <div className="font-display font-black text-lg truncate" data-testid={`request-card-title-${req.id}`}>{distTitle}</div>
                <div className="text-xs opacity-70 mt-0.5 flex items-center gap-3 flex-wrap">
                    <span>Solicitud: {req.created_at?.slice(0, 10)}</span>
                    {req.scheduled_date && <span>· Programada: {req.scheduled_date}</span>}
                    {req.industry && <span>· {req.industry}</span>}
                </div>
                {req.admin_comment && isRejected && (
                    <div className="mt-2 text-xs p-2" style={{ background: 'rgba(239,68,68,0.06)', color: '#7f1d1d' }}>
                        <strong>Observación del admin:</strong> {req.admin_comment}
                    </div>
                )}
                {/* Iter 66 · Client-safe warning: N critical items in this audit's scope.
                    Details (section, question, id) are intentionally hidden — only the
                    count is exposed. Shown for audits that are NOT yet approved OR that
                    were rejected due to a blocking item. */}
                {(req.blocking_items_count || 0) > 0 && !isApproved && (
                    <div className="mt-2 text-xs p-3 border-l-4 flex gap-2"
                        style={{ background: 'rgba(220,38,38,0.06)', borderLeftColor: '#DC2626', color: '#7f1d1d' }}
                        data-testid={`client-blocking-warning-${req.id}`}>
                        <span className="shrink-0" aria-hidden="true">⚠️</span>
                        <div>
                            Esta auditoría incluye <strong>{req.blocking_items_count} ítem{req.blocking_items_count === 1 ? '' : 's'} crítico{req.blocking_items_count === 1 ? '' : 's'}</strong>.
                            {' '}Existe un punto de seguridad que debe cumplirse siempre. Si en el momento de la visita no está en orden,
                            la auditoría se marca como no aprobada automáticamente, sin importar el resultado del resto del checklist.
                            Te recomendamos revisar con tu equipo las condiciones generales de seguridad antes de la fecha programada.
                        </div>
                    </div>
                )}
            </div>
            <div className="flex flex-wrap gap-2 shrink-0">
                {(isInProgress || isApproved || isRejected) && (
                    <button onClick={() => onOpen('checklist')}
                        data-testid={`open-checklist-${req.id}`}
                        className="text-white px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5"
                        style={{ background: isApproved ? '#00875A' : isRejected ? '#6B7280' : '#F59E0B' }}>
                        <FileText className="w-3.5 h-3.5" /> Ver checklist
                    </button>
                )}
                {isApproved && (
                    <button onClick={() => onOpen('certificate')}
                        data-testid={`open-certificate-${req.id}`}
                        className="text-white px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5"
                        style={{ background: '#00875A' }}>
                        <FileDown className="w-3.5 h-3.5" /> Distintivo + QR
                    </button>
                )}
                {isApproved && (
                    <button onClick={async () => {
                        try {
                            const r = await api.get(`/certifications/${req.id}/monthly-receipt.pdf`, { responseType: 'blob' });
                            const url = URL.createObjectURL(r.data);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `comprobante-mensual-${distTitle.replace(/[ :]/g, '_')}.pdf`;
                            a.click();
                            URL.revokeObjectURL(url);
                        } catch (e) {
                            toast.error(apiErrorMessage(e, 'No se pudo descargar el comprobante'));
                        }
                    }}
                        data-testid={`open-monthly-receipt-${req.id}`}
                        className="border-2 px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5"
                        style={{ borderColor: '#00875A', color: '#00875A' }}>
                        <FileDown className="w-3.5 h-3.5" /> Comprobante mensual
                    </button>
                )}
                {isRejected && (
                    <button onClick={() => onOpen('action-plan')}
                        data-testid={`open-action-plan-${req.id}`}
                        className="text-white px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5"
                        style={{ background: '#EF4444' }}>
                        <Sparkles className="w-3.5 h-3.5" /> Recomendaciones IA
                    </button>
                )}
                <button onClick={() => onOpen('details')}
                    data-testid={`open-details-${req.id}`}
                    className="border-2 px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5"
                    style={{ borderColor: 'var(--border)' }}>
                    Detalles <ArrowRight className="w-3.5 h-3.5" />
                </button>
            </div>
        </div>
    );
}

function DetailModal({ type, req, distLookup, onClose }) {
    const [downloading, setDownloading] = useState(false);
    const hasScore = req.score != null || req.status === 'in_review' || req.admin_decision;
    const { title: distTitle } = getDisplayTitle(req, distLookup);

    const downloadReport = async () => {
        setDownloading(true);
        try {
            const r = await api.get(`/certifications/${req.id}/report.pdf`, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `reporte-auditoria-${distTitle.replace(/[ :]/g, '_')}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo descargar el reporte'));
        } finally {
            setDownloading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}
            data-testid="client-detail-modal">
            <div className="max-w-4xl w-full max-h-[92vh] overflow-hidden flex flex-col border"
                style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}
                onClick={(e) => e.stopPropagation()}>
                <div className="p-5 border-b flex items-center justify-between shrink-0 gap-3" style={{ borderColor: 'var(--border)' }}>
                    <div className="min-w-0">
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">
                            {type === 'checklist' && 'Checklist de la auditoría · Solo lectura'}
                            {type === 'certificate' && 'Distintivo aprobado'}
                            {type === 'action-plan' && 'Plan de acción con IA'}
                            {type === 'details' && 'Detalle completo'}
                        </div>
                        <div className="font-display font-black text-2xl truncate" data-testid="detail-modal-title">{distTitle}</div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {type === 'checklist' && hasScore && (
                            <button onClick={downloadReport} disabled={downloading}
                                data-testid="download-audit-report-btn"
                                className="text-white px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 disabled:opacity-50"
                                style={{ background: '#7C6BEF' }}
                                title="Descargar reporte técnico con evidencias, IA y recomendaciones">
                                {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                                <span className="hidden sm:inline">Descargar reporte PDF</span>
                                <span className="sm:hidden">PDF</span>
                            </button>
                        )}
                        <button onClick={onClose} className="p-2 hover:bg-black/5" data-testid="close-detail-modal"><X className="w-5 h-5" /></button>
                    </div>
                </div>
                <div className="p-5 overflow-y-auto flex-1 min-h-0">
                    {type === 'checklist' && <ChecklistReadOnly req={req} />}
                    {type === 'certificate' && <CertificateSection req={req} distLookup={distLookup} />}
                    {type === 'action-plan' && <ActionPlanSection req={req} />}
                    {type === 'details' && <DetailsSection req={req} />}
                </div>
            </div>
        </div>
    );
}

function ChecklistReadOnly({ req }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [openIdx, setOpenIdx] = useState(null);

    useEffect(() => {
        api.get(`/certifications/${req.id}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [req.id]);

    if (loading) return <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
    if (!data) return <div className="p-6 opacity-60 text-center">No se pudo cargar el checklist.</div>;

    const checklist = data.checklist;
    const questions = checklist?.questions || checklist?.items || [];
    const submitted = checklist?.submitted;
    const threshold = data.standard?.approval_threshold || 80;
    const scoreValue = data.score_data?.score ?? checklist?.score;

    // Contadores para el resumen
    const counts = questions.reduce((acc, q) => {
        const a = q.answer;
        if (a === 'yes') acc.yes += 1;
        else if (a === 'no') acc.no += 1;
        else if (a === 'na') acc.na += 1;
        else acc.pending += 1;
        return acc;
    }, { yes: 0, no: 0, na: 0, pending: 0 });

    const failedItems = questions.filter((q) => q.answer === 'no');
    const withEvidence = questions.filter((q) => (q.evidences || []).length > 0).length;

    return (
        <div className="space-y-4">
            <div className="p-3 border-l-4 flex items-start gap-2 text-xs"
                style={{ background: 'rgba(31,143,184,0.06)', borderColor: '#1F8FB8' }}>
                <Info className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#1F8FB8' }} />
                <div>
                    <b>Solo lectura.</b> Este es el checklist completo trabajado por el auditor asignado.
                    Puedes revisarlo, ver las observaciones y evidencias que dejó, y detectar los puntos a mejorar para tu próxima auditoría.
                </div>
            </div>

            {/* Resumen: score + conteos */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {scoreValue != null && (
                    <div className="p-3 border col-span-2 md:col-span-1"
                        style={{ borderColor: scoreValue >= threshold ? '#00875A' : '#EF4444', background: scoreValue >= threshold ? 'rgba(0,135,90,0.05)' : 'rgba(239,68,68,0.05)' }}>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Score</div>
                        <div className="text-2xl font-display font-black" style={{ color: scoreValue >= threshold ? '#00875A' : '#EF4444' }}>
                            {scoreValue.toFixed(1)}%
                        </div>
                        <div className="text-[10px] opacity-60">umbral {threshold}%</div>
                    </div>
                )}
                <SumBox label="Cumple" value={counts.yes} color="#00875A" />
                <SumBox label="No cumple" value={counts.no} color="#EF4444" />
                <SumBox label="N/A" value={counts.na} color="#6B7280" />
                <SumBox label="Pendiente" value={counts.pending} color="#F59E0B" />
            </div>

            {/* Puntos a mejorar (destacado si hay No cumple) */}
            {failedItems.length > 0 && (
                <div className="p-4 border-2" style={{ borderColor: '#EF4444', background: 'rgba(239,68,68,0.05)' }}
                    data-testid="checklist-failed-summary">
                    <div className="flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-5 h-5" style={{ color: '#EF4444' }} />
                        <div className="font-display font-black text-lg" style={{ color: '#EF4444' }}>
                            Puntos a mejorar para próximas auditorías ({failedItems.length})
                        </div>
                    </div>
                    <ul className="text-xs space-y-1.5 ml-4 list-disc">
                        {failedItems.slice(0, 8).map((q, i) => (
                            <li key={i}>
                                <span className="font-semibold">{q.question}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Metadata */}
            <div className="text-[11px] opacity-70 flex flex-wrap gap-3">
                <span>Total de preguntas: <b>{questions.length}</b></span>
                <span>· Con evidencia fotográfica: <b>{withEvidence}</b></span>
                <span>· Estado: <b>{submitted ? 'Enviado' : 'En curso'}</b></span>
            </div>

            {questions.length === 0 ? (
                <div className="p-6 opacity-60 text-center border" style={{ borderColor: 'var(--border)' }}>
                    Aún no hay checklist cargado para esta auditoría.
                </div>
            ) : (
                <div className="space-y-2" data-testid="checklist-items-readonly">
                    {questions.map((q, idx) => {
                        const ans = q.answer;
                        const ansLabel = ans === 'yes' ? '✓ Cumple' : ans === 'no' ? '✗ No cumple' : ans === 'na' ? 'N/A' : '— Pendiente';
                        const ansColor = ans === 'yes' ? '#00875A' : ans === 'no' ? '#EF4444' : ans === 'na' ? '#6B7280' : '#F59E0B';
                        const evs = q.evidences || [];
                        const hasNote = (q.observations || '').trim().length > 0;
                        const isOpen = openIdx === idx;
                        const hasDetails = hasNote || evs.length > 0;
                        return (
                            <div key={q.id || idx} className="border" style={{ borderColor: 'var(--border)', borderLeftWidth: 4, borderLeftColor: ansColor }}>
                                <button
                                    onClick={() => hasDetails && setOpenIdx(isOpen ? null : idx)}
                                    disabled={!hasDetails}
                                    data-testid={`checklist-item-${idx}`}
                                    className="w-full text-left p-3 flex items-start justify-between gap-3 hover:bg-black/[0.02] disabled:cursor-default">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                                            <span className="text-[10px] uppercase tracking-widest font-bold opacity-60">
                                                #{idx + 1}
                                            </span>
                                            {q.weight != null && (
                                                <span className="text-[10px] opacity-60">Peso {typeof q.weight === 'number' ? q.weight.toFixed(1) : q.weight}%</span>
                                            )}
                                            {evs.length > 0 && (
                                                <span className="text-[10px] font-bold inline-flex items-center gap-0.5" style={{ color: '#7C6BEF' }}>
                                                    <ImageIcon className="w-3 h-3" /> {evs.length}
                                                </span>
                                            )}
                                        </div>
                                        <div className="text-sm font-semibold">{q.question}</div>
                                        {hasNote && !isOpen && (
                                            <div className="text-xs opacity-70 mt-1 italic truncate">
                                                “{q.observations}”
                                            </div>
                                        )}
                                    </div>
                                    <span className="text-xs font-bold px-2 py-1 shrink-0 whitespace-nowrap"
                                        style={{ background: `${ansColor}15`, color: ansColor }}>
                                        {ansLabel}
                                    </span>
                                </button>

                                {isOpen && hasDetails && (
                                    <div className="border-t px-3 pb-3 pt-2 space-y-3" style={{ borderColor: 'var(--border)' }}>
                                        {hasNote && (
                                            <div>
                                                <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
                                                    Observaciones del auditor
                                                </div>
                                                <div className="text-sm p-2" style={{ background: 'rgba(0,0,0,0.03)' }}>
                                                    {q.observations}
                                                </div>
                                            </div>
                                        )}
                                        {evs.length > 0 && (
                                            <div>
                                                <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
                                                    Evidencias fotográficas ({evs.length})
                                                </div>
                                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                                    {evs.map((ev, i) => (
                                                        <div key={ev.id || i} className="border" style={{ borderColor: 'var(--border)' }}>
                                                            {ev.storage_path || ev.photo_url ? (
                                                                <img src={ev.photo_url || `/api${ev.storage_path}`}
                                                                    alt={`evidencia-${i}`}
                                                                    className="w-full h-32 object-cover" />
                                                            ) : (
                                                                <div className="w-full h-32 flex items-center justify-center opacity-40">
                                                                    <Camera className="w-6 h-6" />
                                                                </div>
                                                            )}
                                                            {ev.ai_analysis && (
                                                                <div className="p-2 text-[11px] opacity-80">
                                                                    <b>IA:</b> {ev.ai_analysis.slice(0, 160)}{ev.ai_analysis.length > 160 ? '…' : ''}
                                                                </div>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function SumBox({ label, value, color }) {
    return (
        <div className="p-3 border" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">{label}</div>
            <div className="text-2xl font-display font-black" style={{ color }}>{value}</div>
        </div>
    );
}

function CertificateSection({ req, distLookup }) {
    const [downloading, setDownloading] = useState(false);
    const [certToken, setCertToken] = useState(null);
    const { title: distTitle } = getDisplayTitle(req, distLookup);

    // Descargar PDF certificado
    const downloadPDF = async () => {
        setDownloading(true);
        try {
            const r = await api.post(`/certifications/${req.id}/certificate`, {}, { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `distintivo_${distTitle}.pdf`.replace(/[ :]/g, '_');
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo descargar el distintivo'));
        } finally { setDownloading(false); }
    };

    // Obtener token para link público de verificación
    useEffect(() => {
        // Buscar token si ya se generó
        api.get(`/certifications/${req.id}`)
            .then((r) => setCertToken(r.data?.certificate?.token))
            .catch(() => {});
    }, [req.id]);

    return (
        <div className="space-y-4">
            <div className="p-5 border-2 flex items-center gap-4" style={{ borderColor: '#00875A', background: 'rgba(0,135,90,0.05)' }}>
                <div className="w-14 h-14 flex items-center justify-center text-white shrink-0" style={{ background: '#00875A' }}>
                    <ShieldCheck className="w-8 h-8" />
                </div>
                <div className="flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-bold" style={{ color: '#00875A' }}>Distintivo vigente</div>
                    <div className="font-display font-black text-2xl">{distTitle}</div>
                    <div className="text-xs opacity-70">
                        Score: {req.score != null ? `${req.score.toFixed(1)}%` : '—'} · Emitido: {req.admin_decided_at?.slice(0, 10) || req.updated_at?.slice(0, 10)}
                    </div>
                </div>
            </div>

            <div className="flex gap-2 flex-wrap">
                <button onClick={downloadPDF} disabled={downloading}
                    data-testid="download-cert-pdf"
                    className="text-white px-4 py-2.5 font-bold inline-flex items-center gap-2 disabled:opacity-50"
                    style={{ background: '#00875A' }}>
                    {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                    Descargar Distintivo PDF (con QR)
                </button>
                {certToken && (
                    <a href={`/verificar/${certToken}`} target="_blank" rel="noreferrer"
                        data-testid="cert-verify-link"
                        className="border-2 px-4 py-2.5 font-bold inline-flex items-center gap-2 hover:bg-black/5"
                        style={{ borderColor: 'var(--border)' }}>
                        <ExternalLink className="w-4 h-4" /> Ver página pública de verificación
                    </a>
                )}
            </div>

            <div className="p-3 text-xs opacity-80 border-l-4" style={{ background: 'rgba(31,143,184,0.05)', borderColor: '#1F8FB8' }}>
                💡 El PDF incluye código QR con firma criptográfica HMAC-SHA256. Compártelo con tus clientes finales — cualquiera puede escanearlo para validar la autenticidad en tiempo real, sin necesidad de cuenta.
            </div>
        </div>
    );
}

function ActionPlanSection({ req }) {
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [generated, setGenerated] = useState(false);

    const generate = async (force = false) => {
        setLoading(true);
        try {
            const r = await api.post(`/certifications/${req.id}/action-plan${force ? '?force=true' : ''}`);
            setPlan(r.data);
            setGenerated(true);
            if (force) toast.success('Diagnóstico regenerado con la información más reciente');
        } catch (e) {
            toast.error(apiErrorMessage(e, 'No se pudo generar el plan'));
        } finally { setLoading(false); }
    };

    // NO auto-generar. El plan solo se crea al presionar el botón (evita consumo IA involuntario).

    if (!generated && !loading) {
        return (
            <div className="text-center p-8">
                <Sparkles className="w-12 h-12 mx-auto opacity-40 mb-2" />
                <div className="mb-3 opacity-70">Genera un plan de acción personalizado con IA para lograr la aprobación en tu próxima auditoría.</div>
                <button onClick={() => generate(false)} disabled={loading}
                    data-testid="generate-action-plan-btn"
                    className="text-white px-5 py-3 font-bold inline-flex items-center gap-2"
                    style={{ background: '#EF4444' }}>
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                    Generar recomendaciones IA
                </button>
            </div>
        );
    }

    if (loading || !plan) return <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

    return (
        <div className="space-y-4" data-testid="action-plan-result">
            {/* Fuente 1: Comentario del administrador */}
            {plan.admin_comment && (
                <div className="p-4 border-l-4" style={{ background: 'rgba(124,107,239,0.06)', borderColor: '#7C6BEF' }}
                    data-testid="plan-source-admin">
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#7C6BEF' }}>
                        ◆ Visto bueno del Administrador
                    </div>
                    <div className="text-sm">{plan.admin_comment}</div>
                </div>
            )}

            {/* Fuente 2: Observaciones del inspector */}
            {plan.inspector_notes?.length > 0 && (
                <div className="p-4 border-l-4" style={{ background: 'rgba(0,135,90,0.06)', borderColor: '#00875A' }}
                    data-testid="plan-source-inspector">
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-2" style={{ color: '#00875A' }}>
                        ◆ Observaciones del Auditor{plan.inspector?.name ? ` — ${plan.inspector.name}` : ''}
                    </div>
                    <ul className="space-y-1.5 text-sm">
                        {plan.inspector_notes.slice(0, 6).map((n, i) => (
                            <li key={i} className="border-l-2 pl-2" style={{ borderColor: n.flag === 'critical' ? '#EF4444' : n.flag === 'warning' ? '#F59E0B' : '#00875A' }}>
                                <div className="text-[11px] font-bold opacity-80">{n.question}</div>
                                <div className="text-xs">{n.text}</div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* Fuente 3: Síntesis IA */}
            {plan.summary && (
                <div className="p-4 border-l-4 text-sm" style={{ background: 'rgba(239,68,68,0.05)', borderColor: '#EF4444' }}
                    data-testid="plan-source-ia">
                    <div className="text-[10px] uppercase tracking-widest font-bold mb-1" style={{ color: '#EF4444' }}>
                        ◆ Diagnóstico Integrado (IA + Admin + Auditor)
                    </div>
                    {plan.summary}
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <StatBox label="Score proyectado" value={plan.projected_score != null ? `${plan.projected_score}%` : '—'} color="#00875A" />
                <StatBox label="Re-auditoría en" value={plan.next_inspection_recommended_in_days ? `${plan.next_inspection_recommended_in_days} días` : '—'} color="#1F8FB8" />
                <StatBox label="Acciones sugeridas" value={plan.actions?.length || 0} color="#F59E0B" />
            </div>

            {plan.actions?.length > 0 && (
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-2">Acciones ordenadas por prioridad (fuente combinada)</div>
                    <div className="space-y-2">
                        {plan.actions.map((a, i) => (
                            <ActionCard key={i} idx={i + 1} action={a} />
                        ))}
                    </div>
                </div>
            )}

            <button onClick={() => generate(true)} disabled={loading}
                data-testid="regenerate-action-plan-btn"
                className="text-xs opacity-70 hover:opacity-100 inline-flex items-center gap-1"
                title="Regenerar con IA">
                {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />} Regenerar recomendaciones
            </button>
        </div>
    );
}

function StatBox({ label, value, color }) {
    return (
        <div className="p-3 border" style={{ borderColor: 'var(--border)' }}>
            <div className="text-[9px] uppercase tracking-widest font-bold opacity-60">{label}</div>
            <div className="text-lg font-display font-black" style={{ color }}>{value}</div>
        </div>
    );
}

function ActionCard({ idx, action }) {
    const prio = action.priority || 'media';
    const prioColor = prio === 'alta' ? '#EF4444' : prio === 'media' ? '#F59E0B' : '#6B7280';
    const source = action.source || 'ia';
    const sourceMeta = source === 'admin'
        ? { label: 'ADMIN', color: '#7C6BEF' }
        : source === 'inspector'
            ? { label: 'INSPECTOR', color: '#00875A' }
            : source === 'combinado'
                ? { label: 'COMBINADO', color: '#0EA5E9' }
                : { label: 'IA', color: '#EF4444' };
    return (
        <div className="border-l-4 p-3" style={{ borderColor: prioColor, background: `${prioColor}08` }} data-testid={`action-item-${idx}`}>
            <div className="flex items-start gap-3">
                <div className="shrink-0 w-7 h-7 rounded-full text-white flex items-center justify-center text-xs font-bold" style={{ background: prioColor }}>
                    {idx}
                </div>
                <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5" style={{ background: `${prioColor}25`, color: prioColor }}>
                            {prio}
                        </span>
                        <span className="text-[9px] uppercase tracking-widest font-bold px-1.5 py-0.5" style={{ background: `${sourceMeta.color}25`, color: sourceMeta.color }}>
                            fuente: {sourceMeta.label}
                        </span>
                        {action.estimated_days != null && (
                            <span className="text-[10px] uppercase tracking-widest font-bold opacity-60">{action.estimated_days} días</span>
                        )}
                    </div>
                    <div className="font-bold text-sm mb-1">{action.title}</div>
                    {(action.whats_wrong || action.how_to_fix || action.what_you_need || action.how_to_verify) ? (
                        <div className="space-y-1.5 mt-1">
                            {action.whats_wrong && (
                                <div className="text-xs opacity-90"><span className="font-bold" style={{ color: '#EF4444' }}>Qué está mal: </span>{action.whats_wrong}</div>
                            )}
                            {action.how_to_fix && (
                                <div className="text-xs opacity-90"><span className="font-bold" style={{ color: '#0EA5E9' }}>Cómo corregirlo: </span>{action.how_to_fix}</div>
                            )}
                            {action.what_you_need && (
                                <div className="text-xs opacity-90"><span className="font-bold" style={{ color: '#F59E0B' }}>Qué necesitas: </span>{action.what_you_need}</div>
                            )}
                            {action.how_to_verify && (
                                <div className="text-xs opacity-90"><span className="font-bold" style={{ color: '#00875A' }}>Cómo se verifica: </span>{action.how_to_verify}</div>
                            )}
                        </div>
                    ) : (
                        <div className="text-xs opacity-90">{action.description}</div>
                    )}
                    {action.legal_reference && (
                        <div className="text-[10px] italic opacity-70 mt-1">Fundamento: {action.legal_reference}</div>
                    )}
                    {action.expected_impact && (
                        <div className="text-[10px] mt-1" style={{ color: '#00875A' }}>Impacto esperado: {action.expected_impact}</div>
                    )}
                </div>
            </div>
        </div>
    );
}

function DetailsSection({ req }) {
    const [data, setData] = useState(null);
    useEffect(() => {
        api.get(`/certifications/${req.id}`).then((r) => setData(r.data)).catch(() => {});
    }, [req.id]);
    if (!data) return <div className="p-12 text-center opacity-60"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;

    return (
        <div className="space-y-3 text-sm">
            {/* Iter 98 · Desglose aprobado/no aprobado POR distintivo */}
            {Array.isArray(data.verdict?.per_distintivo) && data.verdict.per_distintivo.length > 0 && (
                <div className="border" style={{ borderColor: 'var(--border)' }} data-testid="client-verdict-breakdown">
                    <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)', background: 'rgba(124,107,239,0.06)' }}>
                        <span className="text-[11px] uppercase tracking-widest font-bold" style={{ color: '#7C6BEF' }}>Resultado por distintivo</span>
                        {data.verdict.per_distintivo.length > 1 && (
                            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full"
                                style={{ background: data.verdict.overall_decision === 'approved' ? 'rgba(0,135,90,0.12)' : 'rgba(239,68,68,0.12)', color: data.verdict.overall_decision === 'approved' ? '#00875A' : '#EF4444' }}>
                                Global: {data.verdict.overall_decision === 'approved' ? 'APROBADO' : 'NO APROBADO'}
                            </span>
                        )}
                    </div>
                    <div className="divide-y" style={{ borderColor: 'var(--border)' }}>
                        {data.verdict.per_distintivo.map((r) => (
                            <div key={r.distintivo_id || r.distintivo_code} className="px-4 py-2.5 flex items-center justify-between gap-3"
                                data-testid={`client-verdict-${r.distintivo_code || 'legacy'}`}>
                                <div className="min-w-0">
                                    <div className="text-sm font-bold truncate">{r.distintivo_name || r.distintivo_code}</div>
                                    <div className="text-[11px] opacity-60 tabular-nums">{r.score?.toFixed(1)}% · umbral {r.threshold?.toFixed(0)}%</div>
                                </div>
                                <span className="text-xs font-black px-2.5 py-1 rounded-full shrink-0 inline-flex items-center gap-1"
                                    style={{ background: r.approved ? 'rgba(0,135,90,0.12)' : 'rgba(239,68,68,0.12)', color: r.approved ? '#00875A' : '#EF4444' }}>
                                    {r.approved ? <CheckCircle2 className="w-3.5 h-3.5" /> : <XCircle className="w-3.5 h-3.5" />}
                                    {r.approved ? 'APROBADO' : (r.blocked_by_critical ? 'NO APROB. · BLOQUEO' : 'NO APROBADO')}
                                </span>
                            </div>
                        ))}
                    </div>
                    <div className="px-4 py-2 text-[11px] opacity-60 border-t" style={{ borderColor: 'var(--border)' }}>
                        Cada distintivo se evalúa por separado con su propio umbral. Puedes obtener unos aunque otro no alcance el mínimo.
                    </div>
                </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 text-xs">
                <StatBox label="Score" value={data.score_data?.score != null ? `${data.score_data.score.toFixed(1)}%` : '—'} color="#00875A" />
                <StatBox label="Fotos IA" value={data.photos_summary?.total || 0} color="#F59E0B" />
            </div>
            {data.inspector && (
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Auditor asignado</div>
                    <div className="font-bold">{data.inspector.name}</div>
                </div>
            )}
            {data.photos?.length > 0 && (
                <div>
                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1">
                        Hallazgos IA ({data.photos.length})
                    </div>
                    <ul className="space-y-1">
                        {data.photos.slice(0, 6).map((p, i) => (
                            <li key={i} className="text-xs p-2 border-l-2"
                                style={{ borderColor: p.ai_flag === 'critical' ? '#EF4444' : p.ai_flag === 'warning' ? '#F59E0B' : '#00875A' }}>
                                <b>{p.ai_flag === 'critical' ? 'Crítico' : p.ai_flag === 'warning' ? 'Advertencia' : 'Hallazgo'}:</b> {(p.ai_analysis || '').slice(0, 200)}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}

function CyclesWidget({ cycles, onRenewal }) {
    return (
        <div className="mb-6" data-testid="cycles-widget">
            <div className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-2">
                Ciclos de distintivo
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {cycles.map((c) => {
                    const pct = Math.min(100, (c.audits_completed / c.total_audits_required) * 100);
                    const failed = c.has_failed_audit;
                    const extended = c.total_audits_required === 12;
                    const completed = c.status === 'completed' || (c.audits_completed >= c.total_audits_required && c.audits_passed >= c.total_audits_required);
                    const color = completed ? '#00875A' : failed ? '#EF4444' : c.avg_score >= (c.threshold || 90) ? '#00875A' : '#F59E0B';
                    return (
                        <div key={c.id} className="border-2 p-4"
                            style={{ borderColor: color, background: `${color}08` }}
                            data-testid={`cycle-${c.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div>
                                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">
                                        Paquete {c.package_code}
                                        {c.is_renewal && ' · Renovación anual'}
                                    </div>
                                    <div className="font-display font-black text-lg">{c.distintivo_name || 'Distintivo'}</div>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Progreso</div>
                                    <div className="text-2xl font-black" style={{ color }}>
                                        {c.audits_completed}/{c.total_audits_required}
                                    </div>
                                </div>
                            </div>

                            {/* Barra de progreso */}
                            <div className="h-2 relative overflow-hidden" style={{ background: 'rgba(0,0,0,0.08)' }}>
                                <div className="absolute inset-y-0 left-0 transition-all"
                                    style={{ width: `${pct}%`, background: color }} />
                            </div>

                            <div className="flex flex-wrap items-center gap-3 mt-3 text-xs">
                                {c.avg_score != null && (
                                    <div>
                                        <span className="opacity-60">Score promedio: </span>
                                        <span className="font-bold" style={{ color }}>{c.avg_score.toFixed(1)}%</span>
                                    </div>
                                )}
                                <div>
                                    <span className="opacity-60">Umbral: </span>
                                    <span className="font-bold">{c.threshold || 90}%</span>
                                </div>
                                <div>
                                    <span className="opacity-60">Modalidad: </span>
                                    <span className="font-bold">
                                        {c.payment_mode === 'monthly' ? 'Iguala mensual' : 'Contado'}
                                    </span>
                                </div>
                                {/* Iter 72 · Cambio 1 URGENTE — precios ocultos, ningún cliente real
                                    tiene un ciclo activo aún; se retira el monto mientras se
                                    redefinen precios reales en /admin/settings/pricing. */}
                            </div>

                            {extended && !completed && (
                                <div className="mt-2 text-[11px] p-2 flex items-start gap-1.5"
                                    style={{ background: 'rgba(239,68,68,0.08)', color: '#EF4444' }}>
                                    <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>Ciclo extendido a 12 auditorías por score bajo. Recupérate en las próximas para emitir el distintivo.</span>
                                </div>
                            )}

                            {/* Iter 72 · Cambio 1 URGENTE — pago deshabilitado temporalmente
                                mientras se redefinen precios reales (nunca aprobados). */}
                            {(c.payment_mode === 'upfront' || c.is_renewal) && !completed && c.paid_amount_mxn < c.total_price_mxn && (
                                <div className="mt-3 text-[11px] p-2.5 flex items-start gap-1.5" data-testid={`pay-cycle-pending-${c.id}`}
                                    style={{ background: 'rgba(0,0,0,0.05)', color: 'inherit', opacity: 0.7 }}>
                                    <Info className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>Cobro en definición. Nuestro equipo te contactará para completar el pago.</span>
                                </div>
                            )}
                            {completed && !c.is_renewal && (
                                <div className="mt-3 space-y-2">
                                    <div className="text-[11px] p-2 flex items-start gap-1.5"
                                        style={{ background: 'rgba(0,135,90,0.08)', color: '#00875A' }}>
                                        <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                        <span>¡Ciclo completo! Solicita tu distintivo AIGIS — la renovación anual estará disponible próximamente.</span>
                                    </div>
                                </div>
                            )}
                            {completed && c.is_renewal && (
                                <div className="mt-2 text-[11px] p-2 flex items-start gap-1.5"
                                    style={{ background: 'rgba(0,135,90,0.08)', color: '#00875A' }}>
                                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                                    <span>Renovación anual completada. Distintivo vigente.</span>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}


function DesiredCertificationSection({ user, distintivos, saving, onToggle }) {
    const currentIds = user?.desired_distintivo_ids || [];
    const selectedStds = distintivos.filter((s) => currentIds.includes(s.id));

    // Only show active in the picker; keep inactive ones as read-only chips if already selected.
    const visibleDistintivos = distintivos
        .filter((s) => s.is_active !== false || currentIds.includes(s.id))
        .sort((a, b) => (a.code || '').localeCompare(b.code || ''));

    return (
        <section className="mb-6 p-6" style={{ background: 'var(--card-bg, #fff)', border: '1px solid var(--border, #E5E7EB)' }}
                 data-testid="desired-certification-section">
            <div className="flex items-start gap-3 mb-4">
                <div className="w-10 h-10 flex items-center justify-center shrink-0" style={{ background: `${ACCENT}18`, color: ACCENT }}>
                    <ShieldCheck className="w-5 h-5" />
                </div>
                <div className="flex-1">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: ACCENT }}>Mi distintivo</div>
                    <h2 className="font-display text-xl font-black">Distintivos a los que deseo acreditarme</h2>
                    <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                        Selecciona uno o más distintivos que quieres obtener. Puedes cambiar tu selección cuando quieras.
                        Con esa base, el sistema arma tu checklist (formulario maestro + secciones específicas del distintivo + sugerencias con IA).
                    </p>
                </div>
            </div>

            {/* Selected chips */}
            {selectedStds.length > 0 && (
                <div className="mb-4 p-3 border" style={{ borderColor: `${ACCENT}30`, background: `${ACCENT}08` }} data-testid="desired-cert-selected">
                    <div className="text-[10px] uppercase tracking-wider font-bold mb-2" style={{ color: ACCENT }}>
                        Seleccionadas ({selectedStds.length})
                    </div>
                    <div className="flex flex-wrap gap-2">
                        {selectedStds.map((s) => {
                            const inactive = s.is_active === false;
                            return (
                                <span key={s.id} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-bold" style={{ background: inactive ? '#DC2626' : ACCENT, color: '#fff' }} title={inactive ? 'Este distintivo fue desactivado del catálogo' : s.code}>
                                    {s.name || s.code}{inactive ? ' (inactivo)' : ''}
                                    <button type="button" onClick={() => onToggle(s.id)} disabled={saving}
                                            data-testid={`cert-chip-remove-${s.id}`}
                                            className="hover:opacity-70 disabled:opacity-40">
                                        <X className="w-3 h-3" />
                                    </button>
                                </span>
                            );
                        })}
                    </div>
                    <div className="mt-3">
                        <Link to={user?.role === 'provider' ? '/provider/requests/new' : '/client/requests/new'}
                              data-testid="start-request-from-desired-cert"
                              className="text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2"
                              style={{ background: ACCENT }}>
                            <Plus className="w-3.5 h-3.5" /> Iniciar solicitud
                        </Link>
                    </div>
                </div>
            )}

            {/* Options — flat grid: TODAS los activos seleccionables (fuente única: /public/distintivos) */}
            {distintivos.length === 0 ? (
                <div className="text-sm text-gray-500">Cargando distintivos disponibles...</div>
            ) : visibleDistintivos.length === 0 ? (
                <div className="text-sm text-gray-500 italic">No hay distintivos activos en el catálogo. Contacta al administrador.</div>
            ) : (
                <div>
                    <div className="text-[11px] uppercase tracking-wider font-bold mb-2" style={{ color: ACCENT }}>
                        Disponibles ({visibleDistintivos.length})
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {visibleDistintivos.map((s) => {
                            const active = currentIds.includes(s.id);
                            const comingSoon = s.coming_soon === true;
                            return (
                                <button
                                    key={s.id}
                                    type="button"
                                    disabled={saving || comingSoon}
                                    onClick={() => onToggle(s.id)}
                                    data-testid={`cert-option-${s.id}`}
                                    title={comingSoon ? 'Próximamente disponible' : s.code}
                                    className="text-left px-4 py-3 border transition-colors disabled:opacity-50"
                                    style={{
                                        borderColor: active ? ACCENT : 'var(--border)',
                                        background: active ? `${ACCENT}12` : 'transparent',
                                        cursor: comingSoon ? 'not-allowed' : 'pointer',
                                    }}
                                >
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <div className="font-bold text-sm truncate" title={s.name}>{s.name || s.code}</div>
                                                {comingSoon && (
                                                    <span className="text-[9px] uppercase font-bold px-1.5 py-0.5 shrink-0"
                                                        style={{ background: '#F59E0B22', color: '#F59E0B' }}
                                                        data-testid={`cert-option-coming-soon-${s.id}`}>
                                                        Próximamente
                                                    </span>
                                                )}
                                            </div>
                                            <div className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2" title={s.code}>{s.code}</div>
                                            {s.category && (
                                                <div className="text-[10px] uppercase tracking-wider font-bold mt-1.5 text-gray-400">{s.category}</div>
                                            )}
                                        </div>
                                        {active && <CheckCircle2 className="w-4 h-4 shrink-0" style={{ color: ACCENT }} />}
                                    </div>
                                </button>
                            );
                        })}
                    </div>
                </div>
            )}
        </section>
    );
}


// Envoltorio: decide que panel mostrar segun el tipo de cuenta.
// El cliente entra siempre a la misma direccion; si su cuenta agrupa varias
// unidades ve el panel corporativo, si no ve su panel individual.
export default function ClientDashboard() {
  const [esCorp, setEsCorp] = React.useState(null);
  React.useEffect(() => {
    api.get('/client/soy-corporativo')
      .then((r) => setEsCorp(!!r.data?.es_corporativo))
      .catch(() => setEsCorp(false));
  }, []);
  if (esCorp === null) return null;
  return esCorp ? <PortalCorporativo /> : <PanelClienteIndividual />;
}
